import React, { useEffect, useState } from 'react';
import type { OperatingHours } from '@/apis/types';
import {
  formatDate,
  formatRanges,
  formatWeeklyTotals,
  groupWeekly,
  openStatus,
  timeZoneLabel,
  todayIn,
  upcomingSpecialHours,
} from './operatingHours';
import styles from './OperatingHours.module.scss';

interface OperatingHoursCardProps {
  hours: OperatingHours | null;
  /** Time zone detected from the address; used when `hours.timezone` is null. */
  detectedTimezone: string | null;
  onEdit: () => void;
  disabled?: boolean;
  /** Fixed clock for tests and stories; otherwise the card ticks each minute. */
  now?: Date;
  /** False while the drawer is closed, which pauses the clock. */
  visible?: boolean;
  /** Lets the form return focus here when the hours editor closes. */
  editButtonRef?: React.Ref<HTMLButtonElement>;
}

const useMinuteClock = (fixed: Date | undefined, active: boolean) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (fixed || !active) return undefined;
    // Catch up straight away when the drawer reopens.
    const tick = () => setNow(new Date());
    const timeout = window.setTimeout(tick, 0);
    const id = window.setInterval(tick, 60_000);
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(id);
    };
  }, [fixed, active]);
  return fixed ?? now;
};

/** Read-only "Operating hours" summary on the Edit location form. */
const OperatingHoursCard: React.FC<OperatingHoursCardProps> = ({
  hours,
  detectedTimezone,
  onEdit,
  disabled = false,
  now: fixedNow,
  visible = true,
  editButtonRef,
}) => {
  const now = useMinuteClock(fixedNow, visible);
  const timezone = hours?.timezone ?? detectedTimezone;
  const effective = hours ? { ...hours, timezone } : null;
  const status = effective && timezone ? openStatus(effective, now) : null;
  const upcoming = effective
    ? upcomingSpecialHours(effective.special_hours, todayIn(timezone, now))
    : [];

  return (
    <section className={styles.card} aria-label='Operating hours'>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>Operating hours</span>
        <button
          ref={editButtonRef}
          type='button'
          className={styles.editButton}
          onClick={onEdit}
          disabled={disabled}
        >
          {hours ? 'Edit' : 'Add'}
        </button>
      </div>

      {!effective ? (
        <div className={styles.cardSubtitle}>
          Not set. Add weekly hours, holidays and special hours.
        </div>
      ) : (
        <>
          <div className={styles.cardSubtitle}>
            {[
              timezone ? timeZoneLabel(timezone, now) : null,
              formatWeeklyTotals(effective.weekly),
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>

          {status && (
            <span
              className={`${styles.statusPill} ${
                status.open ? styles.statusOpen : styles.statusClosed
              }`}
            >
              {status.label}
            </span>
          )}

          <div className={styles.summaryRows}>
            {groupWeekly(effective.weekly).map((group) => (
              <React.Fragment key={group.label}>
                <span className={styles.summaryDay}>{group.label}</span>
                <span className={group.ranges.length === 0 ? styles.muted : ''}>
                  {formatRanges(group.ranges)}
                </span>
              </React.Fragment>
            ))}
          </div>

          {upcoming.length > 0 && (
            <>
              <div className={styles.summaryDivider} />
              <div className={styles.summaryLabel}>
                Holidays and special hours
              </div>
              {upcoming.map(({ entry, occurrence }) => (
                <div className={styles.specialRow} key={entry.id}>
                  <span>
                    {entry.name},{' '}
                    {occurrence[0] === occurrence[1]
                      ? formatDate(occurrence[0], false)
                      : `${formatDate(occurrence[0], false)} to ${formatDate(
                          occurrence[1],
                          false
                        )}`}
                  </span>
                  <span
                    className={
                      entry.closed ? styles.muted : styles.specialHoursText
                    }
                  >
                    {formatRanges(entry.closed ? [] : entry.hours)}
                  </span>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default OperatingHoursCard;
